/**
 * CxCheckPopup — the ONE popup of the Stock-In Safety Engine (design §5.2, §5.3).
 *
 * Opened by StockInForm's STEP 8.7 when /api/cx/check-lines asks about a protected line:
 * a red line (fix it in one tap, type the right value, or "My bill really says this" →
 * typed-name confirmation), a quantity far above usual ("Big order?"), or a line the
 * existing DSF / gate checks would otherwise question afterwards — asked HERE instead, so
 * each line is answered once (rule 4). Price-only ambers ride along when the popup is open
 * anyway, never blocking (decision D1). Every edit is re-judged live with the same
 * cxCheckLine the server uses.
 *
 * The form's own rule holds here too: quantity and TOTAL are what people type, TOTAL WINS,
 * and the price per kg is derived. Nothing is saved here. Save hands each line's final
 * values and decision back to the form; "Back to edit" hands back the edits only (edge 20).
 */
import React, { useMemo, useState } from 'react';
import { cxCheckLine, displayPrice, FAMILY_DISPLAY } from '../utils/cxCheckLine';
import { cxDay, cxMoney } from '../utils/cxGuardForm';

const UNITS_BY_FAMILY = { weight: ['kg', 'g'], volume: ['L', 'ml'], count: ['pcs'] };

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const round2 = (n) => Math.round(Number(n) * 100) / 100;
const round4 = (n) => Math.round(Number(n) * 10000) / 10000;
const sameValues = (a, b) => round4(a.quantity) === round4(b.quantity)
  && String(a.unit) === String(b.unit) && round2(a.total) === round2(b.total);
const normName = (s) => String(s || '').trim().toLowerCase();

export function liveVerdict(line, values) {
  return cxCheckLine(
    { quantity: Number(values.quantity), unit: values.unit, total: Number(values.total), pack: line.pack || undefined },
    { item: { family: line.check.family, name: line.name }, window: line.check.window });
}

function liveFixes(line, values) {
  return (liveVerdict(line, values).fixes || [])
    .map((f) => ({ ...f, per_display: displayPrice(f.basePrice, line.check.family) }));
}

/**
 * PURE. Where a line stands: done (with its decision) or what it still needs.
 * Exported for the tests — decisions D1 and D4 live here, not in the markup.
 */
export function resolveLine(line, st) {
  const changed = !sameValues(st.values, line.values);
  const v = liveVerdict(line, st.values);
  const existing = line.check.existing || {};
  const qtyAmber = Boolean(v.quantity && v.quantity.zone === 'amber');
  if (st.decision === 'red_forced') return { done: true, decision: 'red_forced', verdict: v };
  if (!changed) {
    // D4: DSF rewrites this line the moment any confirmation is sent, so "keep as entered"
    // does not exist — use its correction or change the values.
    if (existing.dsf === 'auto_correct') return { done: false, need: 'dsf_fix', verdict: v };
    if (v.status === 'red' || v.status === 'skip' || existing.gate) return { done: false, need: 'fix_or_force', verdict: v };
    if (st.decision === 'amber_confirmed') return { done: true, decision: 'amber_confirmed', verdict: v };
    if (qtyAmber || existing.dsf === 'confirm') return { done: false, need: 'confirm', verdict: v };
    // D1: a price-only amber saves as it is (amber_auto) unless the person confirms it.
    return { done: true, decision: v.status === 'amber' ? 'amber_auto' : 'green', verdict: v };
  }
  if (v.status === 'red' || v.status === 'skip') return { done: false, need: 'fix_or_force', verdict: v };
  if (qtyAmber && st.decision !== 'amber_confirmed') return { done: false, need: 'confirm', verdict: v };
  return { done: true, decision: 'red_corrected', verdict: v };
}

function fixLabel(fix, line) {
  if (fix.kind === 'price') {
    const fam = FAMILY_DISPLAY[line.check.family];
    return `Use ${money(fix.per_display)} per ${fam ? fam.unit : fix.unit}`;
  }
  if (fix.kind === 'quantity_and_total') return `Use ${fix.quantity} ${fix.unit} for ${money(fix.total)}`;
  return `Use ${fix.quantity} ${fix.unit}`;
}

function bandPosition(perDisplay, usual) {
  if (!usual || !(usual.low > 0) || !(usual.high > 0) || !(perDisplay > 0)) return null;
  const lo = Math.log(usual.low / 4);
  const hi = Math.log(usual.high * 4);
  const pos = (x) => Math.max(0, Math.min(1, (Math.log(x) - lo) / (hi - lo)));
  return { dot: pos(perDisplay), from: pos(usual.low), to: pos(usual.high) };
}

const TONE = {
  green: { border: '#86efac', text: '#166534', dot: '#16a34a' },
  amber: { border: '#fcd34d', text: '#92400e', dot: '#d97706' },
  red: { border: '#fca5a5', text: '#b91c1c', dot: '#dc2626' },
};

function PriceBar({ perDisplay, usual, tone }) {
  const p = bandPosition(perDisplay, usual);
  if (!p) return null;
  return (
    <div style={{ margin: '10px 0 4px' }} aria-hidden="true">
      <div style={{ position: 'relative', height: 8, background: '#f3f4f6', borderRadius: 999 }}>
        <div style={{
          position: 'absolute', left: `${p.from * 100}%`, width: `${Math.max(2, (p.to - p.from) * 100)}%`, top: 0, bottom: 0,
          background: '#dcfce7', border: '1px solid #86efac', borderRadius: 999,
        }} />
        <div style={{
          position: 'absolute', left: `calc(${p.dot * 100}% - 7px)`, top: -3, width: 14, height: 14,
          borderRadius: 999, background: TONE[tone].dot,
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
        <span>too low</span><span>usual</span><span>too high</span>
      </div>
    </div>
  );
}

function LineCard({ line, st, res, onChange }) {
  const v = res.verdict;
  const existing = line.check.existing || {};
  const fam = FAMILY_DISPLAY[line.check.family] || { unit: st.values.unit, factor: 1 };
  const perDisplay = v.base && v.base.price > 0 ? displayPrice(v.base.price, line.check.family) : null;
  const usual = line.check.price ? line.check.price.usual : null;
  const changed = !sameValues(st.values, line.values);
  let tone = 'green';
  if (!res.done) tone = res.need === 'confirm' ? 'amber' : 'red';
  else if (res.decision === 'red_forced') tone = 'red';
  else if (res.decision === 'amber_auto') tone = 'amber';
  const nameOk = [line.name, line.dbName].filter(Boolean).some((n) => normName(n) === normName(st.typed));
  const dsfFix = existing.dsf === 'auto_correct' && existing.dsf_effect && !changed
    ? [{ kind: 'quantity', quantity: round4(existing.dsf_effect.quantity), unit: existing.dsf_effect.unit, total: existing.dsf_effect.total }]
    : null;
  const fixes = (dsfFix || liveFixes(line, st.values)).slice(0, 2);
  const set = (patch) => onChange({ ...st, ...patch });
  const setValues = (values) => set({ values, decision: null, dangerOpen: false });

  let pill = { text: 'Looks right', bg: '#dcfce7', fg: '#166534' };
  if (!res.done && res.need === 'confirm') pill = { text: 'Please check', bg: '#fef3c7', fg: '#92400e' };
  else if (!res.done) pill = { text: 'Needs fixing', bg: '#fee2e2', fg: '#b91c1c' };
  else if (res.decision === 'red_forced') pill = { text: 'Saving as typed', bg: '#fee2e2', fg: '#b91c1c' };
  else if (res.decision === 'amber_auto') pill = { text: 'Price changed?', bg: '#fef3c7', fg: '#92400e' };
  else if (res.decision === 'amber_confirmed') pill = { text: 'Confirmed', bg: '#dcfce7', fg: '#166534' };

  const reason = (v.reasons || []).map((r) => r.message).find(Boolean);
  let confirmText = 'Yes, price changed';
  if (v.quantity && v.quantity.zone === 'amber') confirmText = 'Yes, bulk order';
  else if (existing.dsf === 'confirm' && v.status === 'green') confirmText = 'Yes, this is right';

  return (
    <div data-testid={`cx-line-${line.uid}`} style={{ border: `1px solid ${TONE[tone].border}`, borderRadius: 12, padding: 14, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <b style={{ fontSize: 15, color: '#111827' }}>{line.name}</b>
        <span style={{ fontSize: 12, padding: '2px 10px', borderRadius: 999, background: pill.bg, color: pill.fg, whiteSpace: 'nowrap' }}>{pill.text}</span>
      </div>
      {perDisplay != null && (
        <div style={{ fontSize: 14, color: '#374151', marginTop: 6 }}>
          This works out to <b style={{ color: TONE[tone].text }}>{money(perDisplay)} per {fam.unit}</b>.
          {usual ? <> Usually you pay <b>{money(usual.low)} – {money(usual.high)}</b>.</> : null}
        </div>
      )}
      {!res.done && reason && <div style={{ fontSize: 13, color: TONE[tone].text, marginTop: 4 }}>{reason}</div>}
      {v.quantity && v.quantity.zone !== 'green' && line.check.quantity && line.check.quantity.usual_max_display > 0 && (
        <div style={{ fontSize: 13, color: '#92400e', marginTop: 4 }}>
          Big order? Your biggest usual delivery is {round2(line.check.quantity.usual_max_display)} {fam.unit}.
        </div>
      )}
      {dsfFix && (
        <div style={{ fontSize: 13, color: '#92400e', marginTop: 4 }}>
          The decimal check will save this as {dsfFix[0].quantity} {dsfFix[0].unit}. Use that, or change the values.
        </div>
      )}
      {existing.dsf === 'confirm' && !changed && v.status === 'green' && !res.done && (
        <div style={{ fontSize: 13, color: '#92400e', marginTop: 4 }}>This is unusual against your past bills. Is it right?</div>
      )}
      {existing.gate && !changed && v.status !== 'red' && !res.done && (
        <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 4 }}>This is far outside what you normally pay or buy.</div>
      )}
      <PriceBar perDisplay={perDisplay} usual={usual} tone={tone} />

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
        <label style={fieldLabel}>Quantity
          <input aria-label={`${line.name} quantity`} type="number" inputMode="decimal" min="0" step="any" style={input}
            value={st.values.quantity} onChange={(e) => setValues({ ...st.values, quantity: e.target.value })} />
        </label>
        <label style={fieldLabel}>Unit
          {line.pack ? (
            <span style={{ ...input, display: 'inline-block', color: '#6b7280' }} title={`1 ${st.values.unit} = ${line.pack.quantity} ${line.pack.unit}`}>{st.values.unit}</span>
          ) : (
            <select aria-label={`${line.name} unit`} style={input} value={st.values.unit}
              onChange={(e) => setValues({ ...st.values, unit: e.target.value })}>
              {Array.from(new Set([st.values.unit, ...(UNITS_BY_FAMILY[line.check.family] || [])])).map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          )}
        </label>
        <label style={fieldLabel}>Total paid ₹
          <input aria-label={`${line.name} total`} type="number" inputMode="decimal" min="0" step="any" style={input}
            value={st.values.total} onChange={(e) => setValues({ ...st.values, total: e.target.value })} />
        </label>
      </div>
      {fixes.length > 0 && !(res.done && changed) && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          {fixes.map((f) => (
            <button key={`${f.kind}-${f.quantity}-${f.unit}-${f.total}`} type="button" style={fixBtn}
              onClick={() => setValues({ quantity: f.quantity, unit: f.unit, total: round2(f.total) })}>✓ {fixLabel(f, line)}</button>
          ))}
        </div>
      )}

      {((!res.done && res.need === 'confirm') || (res.done && res.decision === 'amber_auto')) && (
        <div style={{ marginTop: 10 }}>
          <button type="button" style={res.done ? quietBtn : fixBtn} onClick={() => set({ decision: 'amber_confirmed' })}>{confirmText}</button>
        </div>
      )}
      {!res.done && res.need === 'fix_or_force' && !st.dangerOpen && (
        <button type="button" style={linkBtn} onClick={() => set({ dangerOpen: true })}>My bill really says this</button>
      )}
      {!res.done && res.need === 'fix_or_force' && st.dangerOpen && (
        <div role="alert" style={{ marginTop: 10, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: 12 }}>
          <div style={{ color: '#b91c1c', fontWeight: 700 }}>⚠ This will spoil your costs</div>
          <div style={{ color: '#b91c1c', fontSize: 13.5, marginTop: 4 }}>
            {line.name} will cost {perDisplay != null ? money(perDisplay) : 'this price'} per {fam.unit}
            {usual ? ` instead of about ${money(usual.median)}` : ''}.
            {line.check.dishes > 0 ? ` ${line.check.dishes} dish${line.check.dishes === 1 ? '' : 'es'} using it will show the wrong cost.` : ''}
            {' '}Your support team will see this line in red.
          </div>
          <div style={{ fontSize: 13, color: '#374151', marginTop: 8 }}>Type <b>{line.name}</b> to confirm</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <input aria-label={`Type ${line.name} to confirm`} style={{ ...input, flex: 1 }} value={st.typed} placeholder={line.name}
              onChange={(e) => set({ typed: e.target.value })} />
            <button type="button" disabled={!nameOk} style={{ ...dangerBtn, opacity: nameOk ? 1 : 0.5, cursor: nameOk ? 'pointer' : 'not-allowed' }}
              onClick={() => set({ decision: 'red_forced', forcedName: st.typed.trim(), dangerOpen: false })}>Save anyway</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * @param {Array<{uid, name, dbName?, values:{quantity,unit,total}, pack?, check, optional?}>} lines
 *        `check` is the check-lines line (status, price, quantity, window, existing, dishes);
 *        `optional` marks a ride-along price amber — shown, never blocking.
 * @param {(results) => void} onSave    every line: { uid, values, changed, decision, forcedName, verdict }
 * @param {(results) => void} onCancel  the same shape; the form keeps the values and nothing else
 */
export default function CxCheckPopup({ lines, scanUnclear = false, otherTotal = 0, onSave, onCancel }) {
  const [state, setState] = useState(() => Object.fromEntries(lines.map((l) => [l.uid, {
    values: { ...l.values }, decision: null, forcedName: null, typed: '', dangerOpen: false,
  }])));
  const [tried, setTried] = useState(false);
  const results = useMemo(() => lines.map((l) => resolveLine(l, state[l.uid])), [lines, state]);
  const required = lines.filter((l) => !l.optional);
  const optional = lines.filter((l) => l.optional);
  const resOf = (l) => results[lines.indexOf(l)];
  const pending = lines.filter((l, i) => !results[i].done);
  const fixedCount = required.filter((l) => resOf(l).done).length;
  const anyEdit = lines.some((l) => !sameValues(state[l.uid].values, l.values));
  const billTotal = round2(Number(otherTotal) + lines.reduce((s, l) => s + (Number(state[l.uid].values.total) || 0), 0));
  const needFix = lines.filter((l, i) => !results[i].done && results[i].need === 'fix_or_force');
  const canFixAll = needFix.length >= 3 && needFix.every((l) => liveFixes(l, state[l.uid].values).length > 0);
  const serious = required.some((l) => {
    const r = resOf(l);
    return (!r.done && r.need !== 'confirm') || r.decision === 'red_forced' || r.decision === 'red_corrected';
  });

  const finalResult = () => lines.map((l, i) => ({
    uid: l.uid,
    values: { quantity: Number(state[l.uid].values.quantity), unit: state[l.uid].values.unit, total: Number(state[l.uid].values.total) },
    changed: !sameValues(state[l.uid].values, l.values),
    decision: results[i].done ? results[i].decision : null,
    forcedName: state[l.uid].forcedName,
    verdict: results[i].verdict,
  }));
  const save = () => {
    setTried(true);
    if (pending.length) return;
    onSave(finalResult());
  };
  const fixAll = () => setState((prev) => {
    const next = { ...prev };
    for (const l of needFix) {
      const f = liveFixes(l, prev[l.uid].values)[0];
      if (f) next[l.uid] = { ...prev[l.uid], values: { quantity: f.quantity, unit: f.unit, total: round2(f.total) }, decision: null, dangerOpen: false };
    }
    return next;
  });
  const card = (l) => (
    <LineCard key={l.uid} line={l} st={state[l.uid]} res={resOf(l)}
      onChange={(st) => setState((prev) => ({ ...prev, [l.uid]: st }))} />
  );

  const n = required.length;
  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label="Check these lines">
      <div style={modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <h3 style={h3}>{serious ? `${n} item${n === 1 ? '' : 's'} look${n === 1 ? 's' : ''} wrong` : `Please check ${n} item${n === 1 ? '' : 's'}`}</h3>
          <span style={{ fontSize: 12, padding: '2px 10px', borderRadius: 999, background: fixedCount === n ? '#dcfce7' : '#fef3c7', color: fixedCount === n ? '#166534' : '#92400e', whiteSpace: 'nowrap' }}>
            {fixedCount} of {n} fixed
          </span>
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', margin: '-4px 0 14px' }}>
          Tap a suggested fix, or type the correct value. The bar turns green when it looks right.
        </div>
        {scanUnclear && (
          <div style={{ background: '#fef3c7', color: '#92400e', borderRadius: 10, padding: 10, fontSize: 13.5, marginBottom: 12 }}>
            This scan looks unclear. Rescan?
          </div>
        )}
        {canFixAll && <button type="button" style={{ ...fixBtn, marginBottom: 12 }} onClick={fixAll}>✓ Fix all suggested</button>}
        {required.map(card)}
        {optional.length > 0 && (
          <>
            <div style={{ fontSize: 12.5, color: '#6b7280', margin: '4px 0 8px' }}>Also different from your usual price (no action needed):</div>
            {optional.map(card)}
          </>
        )}
        {tried && pending.length > 0 && (
          <div role="status" style={{ color: '#b91c1c', fontSize: 13.5, marginBottom: 8 }}>
            Fix {pending.map((l) => l.name).join(' and ')} first, or tap “My bill really says this”.
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, borderTop: '1px solid #e5e7eb', paddingTop: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#6b7280', flex: '1 1 200px' }}>
            {anyEdit ? `Bill total ${money(billTotal)} · matches your paper bill?` : ' '}
          </span>
          <button type="button" style={secondary} onClick={() => onCancel(finalResult())}>Back to edit</button>
          <button type="button" style={primary} onClick={save}>Save stock-in</button>
        </div>
      </div>
    </div>
  );
}

/**
 * The quiet aids under a matched row (design §4.1): "= ₹40 / kg" and "Last time: 12 kg at
 * ₹38/kg · 18 Sep". `aid` comes from cxRowAid() in utils/cxGuardForm; red "check unit?"
 * only when the price is more than 10× outside the item's category range.
 */
export function CxRowAid({ aid }) {
  if (!aid) return null;
  return (
    <div className="stockin-cx-aid" data-testid="cx-row-aid" style={{ fontSize: 12, color: '#6b7280', marginTop: 2, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {aid.perDisplay != null && (
        <span style={aid.warn ? { color: '#b91c1c', fontWeight: 600 } : undefined}>
          = {cxMoney(aid.perDisplay)} / {aid.unit}{aid.warn ? ' · check unit?' : ''}
        </span>
      )}
      {aid.last && (
        <span>
          Last time: {aid.last.quantity_display > 0 ? `${round2(aid.last.quantity_display)} ${aid.unit} at ` : ''}
          {cxMoney(aid.last.per_display)}/{aid.unit}{aid.last.day ? ` · ${cxDay(aid.last.day)}` : ''}
        </span>
      )}
    </div>
  );
}

// ── styles: the house modal (PiecePopups.jsx) ─────────────────────────────────────────
const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001, padding: 16,
};
const modal = {
  position: 'relative', background: '#fff', borderRadius: 16, padding: '24px 22px 18px', width: '100%',
  maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};
const h3 = { margin: '0 0 10px', fontSize: 17, fontWeight: 700, color: '#111827', lineHeight: 1.35 };
const fieldLabel = { display: 'flex', flexDirection: 'column', fontSize: 12, color: '#6b7280', gap: 4 };
const input = { padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, width: 110, background: '#fff', boxSizing: 'border-box' };
const primary = {
  padding: '10px 18px', background: '#111827', color: '#fff', border: 'none',
  borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const secondary = {
  padding: '10px 18px', background: '#fff', color: '#374151', border: '1px solid #d1d5db',
  borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const fixBtn = {
  padding: '7px 12px', background: '#fff', color: '#166534', border: '1px solid #16a34a',
  borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
};
const quietBtn = { ...fixBtn, color: '#374151', border: '1px solid #d1d5db', fontWeight: 500 };
const dangerBtn = {
  padding: '7px 12px', background: '#fff', color: '#b91c1c', border: '1px solid #dc2626',
  borderRadius: 8, fontSize: 13.5, fontWeight: 600,
};
const linkBtn = {
  marginTop: 10, background: 'none', border: 'none', color: '#6b7280',
  fontSize: 13, textDecoration: 'underline', cursor: 'pointer', padding: 0,
};
